import { formatAmount } from './measure-utils';
import { formatQuantity } from './string-utils';
import { resolveUnit } from './units';

export const MEASUREMENT_SYSTEMS = ['us', 'metric', 'mixed'] as const;

export type MeasurementSystem = (typeof MEASUREMENT_SYSTEMS)[number];
export type MeasurementFamily = 'volume' | 'mass' | 'semantic';

export type DisplayMeasurement = {
  amount: number;
  amountLabel: string;
  unit: string;
  converted: boolean;
};

const MASS_IN_GRAMS: Record<string, number> = {
  g: 1,
  kg: 1000,
  lb: 453.59237
};

export function isMeasurementSystem(
  value: unknown
): value is MeasurementSystem {
  return MEASUREMENT_SYSTEMS.includes(value as MeasurementSystem);
}

function roundedAmount(value: number): number {
  if (value >= 100) return Math.round(value);
  if (value >= 10) return Math.round(value * 10) / 10;
  return Math.round(value * 100) / 100;
}

/** Converts only compatible physical units for presentation; storage is untouched. */
export function displayMeasurement(
  amount: number,
  storedUnit: string,
  system: MeasurementSystem
): DisplayMeasurement {
  if (system === 'mixed' || !Number.isFinite(amount)) {
    return {
      amount,
      amountLabel: formatAmount(amount),
      unit: storedUnit,
      converted: false
    };
  }

  const unit = resolveUnit(storedUnit);
  if (unit?.kind === 'volume' && unit.ml !== undefined) {
    const milliliters = amount * unit.ml;
    const targetUnit =
      system === 'us' ? 'oz' : milliliters >= 1000 ? 'l' : 'ml';
    const targetFactor = resolveUnit(targetUnit)!.ml!;
    const convertedAmount = roundedAmount(milliliters / targetFactor);
    return {
      amount: convertedAmount,
      amountLabel: formatAmount(convertedAmount),
      unit: targetUnit,
      converted: targetUnit !== unit.canonical
    };
  }

  const gramsFactor = MASS_IN_GRAMS[storedUnit];
  if (gramsFactor) {
    const grams = amount * gramsFactor;
    const targetUnit = system === 'us' ? 'lb' : grams >= 1000 ? 'kg' : 'g';
    const convertedAmount = roundedAmount(grams / MASS_IN_GRAMS[targetUnit]);
    return {
      amount: convertedAmount,
      amountLabel: formatAmount(convertedAmount),
      unit: targetUnit,
      converted: targetUnit !== storedUnit
    };
  }

  return {
    amount,
    amountLabel: formatAmount(amount),
    unit: storedUnit,
    converted: false
  };
}

/**
 * Ingredient-quantity display, gated to only convert what's actually safe to
 * convert: a `dash`/`twist`/count-based unit (or a legacy row whose
 * `quantity` wasn't parsed into a number) falls straight through to the
 * original `formatQuantity` text -- converting "2 dash" into an oz value
 * would be meaningless, and free-text quantity_str values (e.g. ".25 Sliced
 * Green Lemon") would lose their descriptive text if run through
 * `displayMeasurement`'s numeric-only formatting.
 */
export function formatQuantityForDisplay(
  quantity: number | null | undefined,
  quantityStr: string | undefined,
  quantityType: string | undefined,
  system: MeasurementSystem
): string {
  if (
    typeof quantity === 'number' &&
    Number.isFinite(quantity) &&
    quantityType
  ) {
    const unit = resolveUnit(quantityType);
    if (unit?.kind === 'volume') {
      const display = displayMeasurement(quantity, quantityType, system);
      return `${display.amountLabel} ${display.unit}`;
    }
  }
  return formatQuantity(quantityStr, quantityType);
}
